import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import StoreNavBar from "./Navbar";
import { AppContext } from "../context";
import { getBasePath, isZerobuildDomain } from "../utils";
import { setUserAsGuest } from "../auth/service";
import { getStoreProjects } from "./service";
import { hasAnyCreditNotes } from "./creditNotes/service";
import { PARTY_SCOPE } from "./creditNotes/apiContract";
import { storeProjectsNavFlags } from "./storeProjectsBootstrap";
import Cart from "./components/Cart";
import AuthModal from "./components/AuthModal";
import ProfileModal from "./components/ProfileModal";

const StoreLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    cart,
    currentUser,
    setCurrentUser,
    isCartOpen,
    toggleCart,
    isAuthModalOpen,
    openAuthModal,
    closeAuthModal,
    isProfileModalOpen,
    openProfileModal,
    closeProfileModal,
    dealerReferral,
    storeProjectsList,
    setStoreProjectsList,
    clearStoreProjectsCache,
  } = useContext(AppContext);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [hasAnyOrder, setHasAnyOrder] = useState(false);
  const [hasAnyQuote, setHasAnyQuote] = useState(false);
  const [hasAnyCreditNote, setHasAnyCreditNote] = useState(false);
  const basePath = getBasePath();
  const cartItemCount = cart.length;

  const isTempGuestUser =
    Boolean(currentUser?.temp === true) ||
    String(currentUser?.mobile ?? "")
      .toLowerCase()
      .startsWith("guest_");
  const isLoggedIn = Boolean(currentUser) && !isTempGuestUser;
  const isDealerByType =
    typeof currentUser?.type === "string" &&
    currentUser.type.toLowerCase().includes("dealer");
  const isDealerUser =
    isLoggedIn && (currentUser?.dealer === true || isDealerByType);
  const isAdminUser = isLoggedIn && currentUser?.admin === true;

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isBuyerUser = isLoggedIn && !isDealerUser && !isAdminUser;

  const projectsNavFlags = useMemo(
    () => storeProjectsNavFlags(storeProjectsList),
    [storeProjectsList]
  );

  const isStoreProjectDetailPage = /^\/projects\/[^/]+/.test(location.pathname);
  const isProductsListingPage = /^\/products\/?$/.test(location.pathname);

  useEffect(() => {
    if (isStoreProjectDetailPage) return;
    if (!isLoggedIn || isDealerUser || isAdminUser) {
      setHasAnyOrder(false);
      setHasAnyQuote(false);
      return;
    }
    setHasAnyOrder(projectsNavFlags.hasAnyOrder);
    setHasAnyQuote(projectsNavFlags.hasAnyQuote);
  }, [
    isStoreProjectDetailPage,
    isLoggedIn,
    isDealerUser,
    isAdminUser,
    projectsNavFlags.hasAnyOrder,
    projectsNavFlags.hasAnyQuote,
  ]);

  useEffect(() => {
    if (!isBuyerUser || isStoreProjectDetailPage || isProductsListingPage) {
      setHasAnyCreditNote(false);
      return undefined;
    }

    let cancelled = false;
    const probeCreditNotes = () => {
      hasAnyCreditNotes(PARTY_SCOPE.DIRECT_BUYER)
        .then((hasCredit) => {
          if (!cancelled) setHasAnyCreditNote(hasCredit);
        })
        .catch(() => {
          if (!cancelled) setHasAnyCreditNote(false);
        });
    };

    let idleId;
    let timeoutId;
    if (typeof requestIdleCallback === "function") {
      idleId = requestIdleCallback(probeCreditNotes, { timeout: 4000 });
    } else {
      timeoutId = setTimeout(probeCreditNotes, 1500);
    }

    return () => {
      cancelled = true;
      if (idleId != null && typeof cancelIdleCallback === "function") {
        cancelIdleCallback(idleId);
      }
      if (timeoutId != null) clearTimeout(timeoutId);
    };
  }, [
    isBuyerUser,
    isStoreProjectDetailPage,
    isProductsListingPage,
    currentUser?.id,
  ]);

  const handleCartClick = async () => {
    if (isTempGuestUser || isDealerUser || isAdminUser) {
      try {
        const res = await getStoreProjects();
        const body = res?.data ?? res;
        const projects = Array.isArray(body)
          ? body
          : Array.isArray(body?.data)
          ? body.data
          : [];
        if (projects.length === 1) {
          const only = projects[0];
          const onlyId = only?.id ?? only?.project_id;
          if (onlyId != null) {
            navigate(`/projects/${onlyId}`, { state: { project: only } });
            return;
          }
        }
      } catch {
        // fall through
      }
      navigate(isAdminUser ? "/admin/dashboard" : "/projects");
      return;
    }
    toggleCart?.();
  };

  const logoutUser = () => {
    setUserAsGuest();
    localStorage.removeItem("storeDealerReferral");
    setCurrentUser(null);
    setStoreProjectsList?.([]);
    clearStoreProjectsCache?.();
    navigate("/");
  };

  const isDesignDetailPage = location.pathname.includes("/designs/");
  const isAccountNotSupportedPage = /\/account-not-supported\/?$/.test(
    location.pathname
  );
  const shouldHideNavbar =
    isStoreProjectDetailPage ||
    isAccountNotSupportedPage ||
    (isDesignDetailPage && isMobile);
  const dealerName = dealerReferral?.dealer?.name;
  const showDealerBanner =
    Boolean(dealerName) && !isDealerUser && !isAccountNotSupportedPage;
  const isAboutPage = /\/about\/?$/.test(location.pathname);
  const isAdminDashboardPage = /\/admin\/dashboard\/?$/.test(location.pathname);
  const isOpsPage = location.pathname.includes("/ops/");
  const isStockPage = location.pathname.includes("/stock/");
  const isCPPage = location.pathname.includes("/cp/");
  const isAdminFullWidthPage =
    isAdminDashboardPage || isOpsPage || isStockPage || isCPPage;
  const useZbColumn =
    isZerobuildDomain() &&
    !isAboutPage &&
    !isAdminFullWidthPage &&
    !isAccountNotSupportedPage;
  const mainPaddingTop =
    shouldHideNavbar || useZbColumn ? "0" : showDealerBanner ? "0" : "60px";

  useEffect(() => {
    document.body.classList.toggle("zb-column-layout", useZbColumn);
    return () => document.body.classList.remove("zb-column-layout");
  }, [useZbColumn]);

  useEffect(() => {
    if (isAccountNotSupportedPage && isAuthModalOpen) closeAuthModal?.();
  }, [isAccountNotSupportedPage, isAuthModalOpen, closeAuthModal]);

  return (
    <div
      className={`store-layout${
        isAdminFullWidthPage || isAccountNotSupportedPage
          ? " w-100 mw-100 min-vh-100 bg-white"
          : useZbColumn
          ? " store-layout--zb-column"
          : ""
      }`}
    >
      {!shouldHideNavbar && (
        <>
          <StoreNavBar
            centeredColumn={useZbColumn}
            onCartClick={handleCartClick}
            onAuthClick={isLoggedIn ? logoutUser : openAuthModal}
            onEditProfileClick={openProfileModal}
            cartItemCount={cartItemCount}
            isLoggedIn={isLoggedIn}
            hasAnyOrder={hasAnyOrder}
            hasAnyQuote={hasAnyQuote}
            hasAnyCreditNote={hasAnyCreditNote}
          />
          {showDealerBanner ? (
            <div
              className="px-3 py-2"
              style={{
                marginTop: useZbColumn ? 0 : 60,
                background: "#d5edda",
              }}
            >
              <span style={{ color: "#6c757d" }}>Dealer: </span>
              <strong>{dealerName}</strong>
            </div>
          ) : null}
        </>
      )}

      <main
        className={`store-main${
          isAdminFullWidthPage || isAccountNotSupportedPage
            ? " w-100 mw-100"
            : ""
        }`}
        style={{ paddingTop: mainPaddingTop }}
      >
        {children}
      </main>

      <Cart isOpen={Boolean(isCartOpen)} toggle={() => toggleCart?.()} />

      <AuthModal isOpen={isAuthModalOpen} toggle={closeAuthModal} />

      <ProfileModal isOpen={isProfileModalOpen} toggle={closeProfileModal} />
    </div>
  );
};

export default StoreLayout;
